import {
  observable, computed, action, makeObservable,
} from 'mobx';
import { html, TemplateResult } from 'lit';

export default class OcclStore {
  private occlVersion = 'v3';
  private occlUri: string;
  private styleContext?: string;

  private themeLoaded = false;
  private vendorsLoaded = false;
  private bundleLoaded = false;
  public error?: Error = undefined;

  public get ready(): boolean {
    return this.themeLoaded && this.vendorsLoaded && this.bundleLoaded;
  }

  public constructor(occlUri: string, styleContext?: string) {
    makeObservable<OcclStore, 'themeLoaded' | 'vendorsLoaded' | 'bundleLoaded' | 'loadScripts'>(this, {
      themeLoaded: observable,
      vendorsLoaded: observable,
      bundleLoaded: observable,
      error: observable,
      ready: computed,
      loadScripts: action,
      renderStylesheet: action,
    });

    this.occlUri = occlUri;
    this.styleContext = styleContext;
    this.loadScripts();
  }

  private loadScripts() {
    const onLoadVendors = action(() => { this.vendorsLoaded = true; });
    const onLoadBundle = action(() => { this.bundleLoaded = true; });
    const onError = () => { this.error = new Error('loading OCCL failed'); };
    const scripts = [{
      src: `${this.occlUri}/${this.occlVersion}/library/vendors.js`,
      module: true, // loaded if browser supports modules
      onLoad: onLoadVendors,
      onError,
    }, {
      src: `${this.occlUri}/${this.occlVersion}/library/vendors-es5.js`,
      module: false, // loaded if browser does *not* support modules
      onLoad: onLoadVendors,
      onError,
    }, {
      src: `${this.occlUri}/${this.occlVersion}/pre-sets/coms/bundle.js`,
      module: true, // loaded if browser supports modules
      namespace: 'coms', // only needed with `bundle.js`
      onLoad: onLoadBundle,
      onError,
    }, {
      src: `${this.occlUri}/${this.occlVersion}/pre-sets/coms/bundle-es5.js`,
      module: false, // loaded if browser does *not* support modules
      namespace: 'coms', // only needed with `bundle.js`
      onLoad: onLoadBundle,
      onError,
    }, {
      src: `${this.occlUri}/${this.occlVersion}/components/modal/bundle.js`,
      module: true,
      onLoad: () => {},
      onError: () => {},
    }];
    scripts.forEach((script) => this.$loadScript(script));
  }

  // eslint-disable-next-line class-methods-use-this
  private $loadScript = (options: {
    src: string, module: boolean, namespace?: string,
    onLoad: () => void, onError: () => void,
  }) => {
    const {
      src, module, namespace, onLoad = () => {}, onError = () => {},
    } = options;
    const script = document.createElement('script');
    script.setAttribute('src', src);
    if (module) {
      script.setAttribute('type', 'module');
    } else {
      script.setAttribute('nomodule', '');
    }
    if (namespace) {
      script.setAttribute('namespace', namespace);
    }
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    document.body.appendChild(script);
  };

  public renderStylesheet(): TemplateResult {
    const themeUri = `${this.occlUri}/${this.occlVersion}/pre-sets/coms/bundle.css`;
    const onLoad = action(() => { this.themeLoaded = true; });
    const onError = action(() => { this.error = new Error('loading OCCL failed'); });
    return html`
      <link rel="stylesheet" href="${themeUri}" @load="${onLoad}" @error="${onError}" />
    `;
  }

  public getStyleContextClasses(): string[] {
    return this.styleContext
      ? this.styleContext.split(' ').filter((s) => s).map((s) => `context-${s}`)
      : [];
  }
}
